<template>
  <div>
    <div class="evollu-form" v-if="showFilters">

      <div class="form-row">
        <div class="col-12 col-md-3">
          <div>
            <label class="form-label">{{ $t('evocall.requests.date_range') }}:</label>
            <RangeDatePicker
              id="selected-time-date-picker"
              v-model="filters.dateRange"
              :placeholder="$t('evocall.requests.date_range')"
              :clearable="false"
            />
          </div>
        </div>

        <div class="col-12 col-md-3">
          <label class="form-label">
            {{ $t('clinical.appointments.dispenser') }}
          </label>
          <multiselect
            id="dispensers-filter"
            v-model="filters.dispensers"
            :options="dispensersOptions"
            track-by="id"
            :customLabel="formatedUserLabel"
            :placeholder="dispensersOptions.length ? $t('clinical.waiting_room_statistics.all_dispensers') : $t('clinical.waiting_room_statistics.no_dispensers')"
            :allow-empty="true"
            :multiple="true"
            :disabled="!dispensersOptions.length"
            :close-on-select="false"
          >

            <template slot="clear">
              <span class="multiselect__clear" v-if="filters.dispensers.length > 0"
                    @mousedown.prevent.stop="filters.dispensers = []"></span>
            </template>
          </multiselect>
        </div>
        <div class="col-12 col-md-3">
          <label class="form-label">
            {{ $t('clinical.canceled_call_requests.cancel_reason') }}
          </label>
          <multiselect
            id="reasons-filter"
            v-model="filters.cancelReasons"
            :options="requestCancelationReasons"
            track-by="id"
            :customLabel="option => $t(`evocall.requests.cancelation_reasons.${option.id}`)"
            placeholder="---"
            :allow-empty="true"
            :multiple="true"
            :close-on-select="false"
          >

            <template slot="clear">
              <span class="multiselect__clear" v-if="filters.cancelReasons.length > 0"
                    @mousedown.prevent.stop="filters.cancelReasons = []"></span>
            </template>
          </multiselect>
        </div>

        <div class="col-12 col-md-3">
          <label class="form-label">
            {{ $t('clinical.appointments.type') }}
          </label>
          <multiselect
            id="appt-type-filter"
            v-model="filters.appointmentTypes"
            :options="appointmentTypesOptions"
            track-by="id"
            label="type"
            placeholder="---"
            :allow-empty="true"
            :multiple="true"
            :close-on-select="false"
          >

            <template slot="clear">
              <span class="multiselect__clear" v-if="filters.appointmentTypes.length > 0" @mousedown.prevent.stop="filters.appointmentTypes = []"></span>
            </template>
          </multiselect>
        </div>
      </div>
    </div>

    <v-server-table
      :columns="columns"
      :options="tableOptions"
      @filter="onFilterUpdate"
      @row-click="onRowClicked"
      id="canceledCallRequestsTable"
      ref="canceledCallRequestsTable"
      name="canceledCallRequestsTable"
    >

      <span v-if="row.created_at" slot="created_at" slot-scope="{row}">
      {{ row.created_at | localTime('YYYY-MM-DD HH:mm') }}
    </span>

      <span v-if="row.canceled_at" slot="canceled_at" slot-scope="{row}">
      {{ row.canceled_at | localTime('YYYY-MM-DD HH:mm') }}
    </span>

      <span slot="request_duration" slot-scope="{row}">
        {{ getRelativeTimeString(getDurationTime(row)) }}
    </span>

      <span slot="canceled_reason" slot-scope="{row}">
      {{  row.canceled_reason && row.canceled_reason.id ? $t(`evocall.requests.cancelation_reasons.${row.canceled_reason.id}`) : "N/A" }}
    </span>

      <span slot="appointmentType" slot-scope="{row}">
        {{ row.appointmentType || "N/A" }}
      </span>

      <span slot="obs" class="no-break observations__row" slot-scope="{row}">
      <div v-if="row.obs">
        <va-popover
          :message="row.obs"
          placement="left"
        >
          <i class="i-has-comment-gray"></i>
        </va-popover>
      </div>
    </span>
    </v-server-table>
  </div>
</template>

<script>
import _, { uniqBy } from 'lodash'
import Multiselect from 'vue-multiselect'
import TableQueryMixin from '@/mixins/TableQueryMixin'
import RelativeTimeMixin from '@/mixins/RelativeTimeMixin'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import { ActivityService } from '@/api/AuthService'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import { mapState } from 'vuex'

const CANCELED_CALL_REQUESTS_INFO_TYPE = 200

export default {
  props: {
    service: {
      type: Object,
      required: true
    },
    showFilters: {
      type: Boolean,
      required: false,
    },
    querySearch: {
      type: String,
      required: false,
      default: null
    },
    customQueries: {
      type: Object,
      required: false,
      default: null
    }
  },
  components: {
    Multiselect,
    RangeDatePicker
  },
  mixins: [
    TableQueryMixin,
    RelativeTimeMixin,
    MultiselectLabelFormatter
  ],
  created: function () {
    this.loadEntityActivityUsersForPeriod(this.filters.dateRange.start, this.filters.dateRange.end)
      .then(() => {
        this.loadDispensers()
      })
      .finally(() => {
        this.reloadTable()
      })
  },
  data () {
    return {
      columns: [
        'created_at',
        'canceled_at',
        'request_duration',
        'customer_number',
        'facility_name',
        'dispenser_nickname',
        'canceled_reason',
        'appointmentType'
      ],
      queryUrl: `${this.service.url}/v1/requests/canceled`,
      tableOptions: {
        columnsDropdown: false,
        saveState: true,
        filterable: true,
        customFilters: ['start', 'end', 'dispensers', 'cancelReasons', 'appointmentTypes'],
        sortable: ['created_at'],
        headings: {
          'created_at': this.$t('evocall.requests.created_at'),
          'canceled_at': this.$t('clinical.canceled_call_requests.canceled_at'),
          'request_duration': this.$t('clinical.canceled_call_requests.request_duration'),
          'customer_number': this.$t('virtual_waiting_room.titles.customer_nr'),
          'facility_name': this.$t('evocall.requests.facility'),
          'dispenser_nickname': this.$t('evocall.requests.creator'),
          'canceled_reason': this.$t('clinical.canceled_call_requests.cancel_reason'),
          'appointmentType': this.$t('clinical.appointments.type')
        },
        columnsClasses: {
          cancel_reason: 'content-center',
        },
        orderBy: {
          column: 'created_at',
          ascending: false
        }
      },
      filters: {
        cancelReasons: [],
        dateRange: {
          start: this.moment().subtract(1, 'months').format('YYYY-MM-DD'),
          end: this.moment().format('YYYY-MM-DD'),
        },
        dispensers: [],
        appointmentTypes: [],
      },
      entityActivityUsersForPeriod: []
    }
  },
  methods: {
    reloadTable: function () {
      const tableView = this.getTableReference()
      if (tableView) {
        tableView.refresh()
      }
    },
    loadDispensers: function () {
      return new Promise((resolve) => {
        if (this.customQueries !== null) {
          if (this.customQueries.hasOwnProperty('dispensers') && typeof this.customQueries.dispensers === 'string') {
            try {
              const dispenserIds = this.customQueries.dispensers.split(',').map(id => parseInt(id))
              this.filters.dispensers = this.dispensersOptions.filter(dispenser => dispenserIds.includes(dispenser.id))
            } catch (errors) {
              console.log(errors)
            }
          }
        }
        resolve()
      })
    },
    getTableReference: function () {
      return this.$refs.canceledCallRequestsTable
    },
    loadEntityActivityUsersForPeriod: function (start, end) {
      return new Promise((resolve, reject) => {
        if (start && end) {
          ActivityService.getActivityUsersByEntity({
            accessType: this.accessTypes.READ,
            serviceId: this.service.id,
            infoType: CANCELED_CALL_REQUESTS_INFO_TYPE,
            start: this.moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            end: this.moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            activities: `${this.activities.DISPENSER_FIELD},${this.activities.DISPENSER_PREMISES}`
          })
            .then(entityActivityRecords => {
              this.entityActivityUsersForPeriod = entityActivityRecords
              resolve()
            }).catch(errors => {
            console.log(errors)
            reject(errors)
          })
        } else {
          reject(new Error('Load Entity Activity Users For Period - Invalid args'))
        }
      })
    },
    getDurationTime: function (request) {
      let start = this.moment(request.created_at)
      let end = this.moment(request.canceled_at)
      return end.diff(start, 'second')
    },
    onRowClicked: function ({ row }) {
      if (row.ended_at !== null) {
        this.$emit('row-clicked', { callRequestAppointment: row })
      }
    },
    getDispenserName: function (appointmentCallRequest) {
      const dispenser = _.get(appointmentCallRequest, 'parent.user', null)
      return dispenser ? dispenser.nickname || dispenser.name : null
    },
  },
  computed: {
    ...mapState('auth', [
      'activities',
      'accessTypes'
    ]),
    ...mapState({
      requestCancelationReasons (state) {
        return Object.values(state[this.waitingRoomModuleKey].requestCancelationReasons) || []
      },
      appointmentTypesOptions: function(state) {
        return state[this.waitingRoomModuleKey].appointmentTypes
      }
    }),
    waitingRoomModuleKey: function () {
      return `virtualWaitingRoom`
    },
    activeFilters: function () {
      return Object.keys(this.filters).reduce((accumulator, currentValue) => accumulator + (Array.isArray(this.filters[currentValue]) ? (this.filters[currentValue].length > 0 ? 1 : 0) : typeof this.filters[currentValue] === 'object' ? Object.values(this.filters[currentValue]).some(value => value) ? 1 : 0 : this.filters[currentValue] !== '' ? 1 : 0), 0)
    },
    dispensersOptions: function () {
      const dispenserActivities = [this.activities.DISPENSER_FIELD, this.activities.DISPENSER_PREMISES]
      return uniqBy(this.entityActivityUsersForPeriod
        .filter(entityActivityUser => dispenserActivities.includes(entityActivityUser.activity.id) && entityActivityUser.hasOwnProperty('user') && entityActivityUser.user !== null)
        .map(entityActivityUser => entityActivityUser.user), 'id')
    }
  },
  watch: {
    querySearch: {
      handler: function (value) {
        if (this.$refs.hasOwnProperty('canceledCallRequestsTable')) {
          this.$refs.canceledCallRequestsTable.setFilter(value)
        }
      },
      immediate: true
    },
    customQueries: {
      handler: function (value) {
        if (value && value.start && value.start !== '') {
          this.filters.dateRange.start = value.start
        } else {
          this.$nextTick(function () {
            this.$refs.canceledCallRequestsTable.customQueries.start = this.moment().subtract(1, 'months').format('YYYY-MM-DD')
          })
        }

        if (value && value.end && value.end !== '') {
          this.filters.dateRange.end = value.end
        } else {
          this.$nextTick(function () {
            this.$refs.canceledCallRequestsTable.customQueries.end = this.moment().format('YYYY-MM-DD')
          })
        }
      },
      immediate: true
    },
    'filters.dateRange': function (value) {
      this.$refs.canceledCallRequestsTable.customQueries.start = value.start || ''
      this.$refs.canceledCallRequestsTable.customQueries.end = value.end || ''
      this.emitTableFilter('vue-tables.canceledCallRequestsTable.filter::end', value.end || '')
    },
    'filters.dispensers': function (dispensers) {
      if (Array.isArray(dispensers)) {
        this.emitTableFilter('vue-tables.canceledCallRequestsTable.filter::dispensers', dispensers.map(dispenser => dispenser.id).join(','))
      }
    },
    'filters.cancelReasons': function (reasons) {
      if (Array.isArray(reasons)) {
        this.emitTableFilter('vue-tables.canceledCallRequestsTable.filter::cancelReasons', reasons.map(reason => reason.id).join(','))
      }
    },
    'filters.appointmentTypes': function (appointmentTypes) {
      if (Array.isArray(appointmentTypes)) {
        this.emitTableFilter('vue-tables.canceledCallRequestsTable.filter::appointmentTypes', appointmentTypes.flatMap(type => type.bdIds).join(','))
      }
    },
    activeFilters: {
      handler: function (activeFilters) {
        this.$emit('filters-updated', activeFilters)
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.content-center {
  text-align: center;
}

#callRatingsTable {
  $assetsPath: "../../../assets";
  @import '@/sass/icons/icons-classes.scss';
  $primary-green: #29C4A9;

  tr:hover {
    .va-rating-item__icon {
      color: white !important;
      opacity: 1 !important;
    }

    .va-rating__icon-item--empty {
      opacity: .3 !important;
    }
  }

  tr {
    .va-rating-item__icon {
      color: $primary-green !important;
      opacity: 1 !important;
    }

    .va-rating__icon-item--empty {
      opacity: .3 !important;
    }
  }

  .i-has-comment-gray {
    @extend .i-has-comment-gray;
  }
}
</style>
